import React, { useState } from 'react';
import { Play, Pause, Square, ChevronLeft, ChevronRight, Edit3, Check, ArrowLeft, Camera } from 'lucide-react';

const AutoTestController = ({
  secuenciaSeleccionada,
  posicionesEjecutadas,
  posicionActualIndex,
  ejecutando,
  pausado,
  esperandoAccion,
  isConnected,
  handlePlay,
  handlePause,
  handleStop,
  handleReiniciar,
  onToggleStatus,
  setIndice,
  errorHiloRoto,
  onReparado,
  tiempoRestante,
  clavosRestantes,
  errorClavosAgotados,
  onRecargarClavos,
  isAdmin,
  modoTimelapse,
  setModoTimelapse,
  tiempoEsperaTimelapse,
  setTiempoEsperaTimelapse,
  fotoIntervaloMax,
  setFotoIntervaloMax,
  esperandoTimelapse,
  esHilador
}) => {
  const [isEditingIndex, setIsEditingIndex] = useState(false);
  const [tempIndex, setTempIndex] = useState('');
  const [animationClass, setAnimationClass] = useState('');
  const prevIndexRef = React.useRef(posicionActualIndex);

  // Input de timelapse como string libre: permite borrar todo y escribir decimales.
  // Se valida/clampa solo al salir del campo (blur).
  const [tlInput, setTlInput] = useState(String(tiempoEsperaTimelapse ?? ''));
  React.useEffect(() => {
    setTlInput(String(tiempoEsperaTimelapse ?? ''));
  }, [tiempoEsperaTimelapse]);

  const commitTimelapse = () => {
    let v = parseFloat(tlInput);
    if (isNaN(v)) v = 1;
    v = Math.min(60, Math.max(0.1, v));
    setTiempoEsperaTimelapse(v);
    setTlInput(String(v));
  };

  // Input de iMax (intervalo máximo de fotos) como string libre
  const [imaxInput, setImaxInput] = useState(String(fotoIntervaloMax ?? ''));
  React.useEffect(() => {
    setImaxInput(String(fotoIntervaloMax ?? ''));
  }, [fotoIntervaloMax]);
  const commitImax = () => {
    let v = parseFloat(imaxInput);
    if (isNaN(v)) v = 10;
    v = Math.min(100, Math.max(1, v));
    setFotoIntervaloMax(v);
    setImaxInput(String(v));
  };

  React.useEffect(() => {
    if (posicionActualIndex > prevIndexRef.current) {
      setAnimationClass('animate-slide-right');
    } else if (posicionActualIndex < prevIndexRef.current) {
      setAnimationClass('animate-slide-left');
    } else {
      setAnimationClass('');
    }
    prevIndexRef.current = posicionActualIndex;
  }, [posicionActualIndex]);

  const handleIndexSubmit = (e) => {
    e.preventDefault();
    const newIndex = parseInt(tempIndex) - 1; // Convertir a base 0
    if (!isNaN(newIndex)) {
      setIndice(newIndex);
    }
    setIsEditingIndex(false);
  };
  
  if (!secuenciaSeleccionada) {
    return (
      <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4">
        <div className="text-center py-10">
          <div className="text-4xl mb-2">📋</div>
          <h3 className="text-lg font-semibold text-slate-300 mb-1">Selecciona una secuencia</h3>
          <p className="text-slate-500 text-sm">Elige una secuencia de la lista o crea una nueva</p>
        </div>
      </div>
    );
  }

  if (errorHiloRoto) {
    return (
      <div className="bg-red-900/90 p-6 rounded-xl border-2 border-red-500 text-center animate-pulse shadow-2xl">
        <h2 className="text-3xl font-bold text-white mb-2">⚠️ HILO ROTO DETECTADO</h2>
        <p className="text-red-200 mb-6 text-lg">El encoder no detectó movimiento suficiente por 2 veces consecutivas.</p>
        <button 
          onClick={onReparado}
          className="bg-white text-red-900 font-bold py-4 px-8 rounded-full text-xl shadow-lg hover:scale-105 transition-transform active:scale-95"
        >
          ✅ YA LO REPARÉ
        </button>
      </div>
    );
  }

  if (errorClavosAgotados) {
    return (
      <div className="bg-orange-900/90 p-6 rounded-xl border-2 border-orange-500 text-center shadow-2xl">
        <h2 className="text-3xl font-bold text-white mb-2">🔨 SE HAN ACABADO LOS CLAVOS</h2>
        <p className="text-orange-200 mb-6 text-lg">Se han utilizado los 90 clavos del cargador.</p>
        <button 
          onClick={onRecargarClavos}
          className="bg-white text-orange-900 font-bold py-4 px-8 rounded-full text-xl shadow-lg hover:scale-105 transition-transform active:scale-95"
        >
          ♻️ RECARGAR 90
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 ">
      <style>{`
        @keyframes slideInRight {
          0% { transform: translateX(50px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInLeft {
          0% { transform: translateX(-50px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
        .animate-slide-left {
          animation: slideInLeft 0.3s ease-out forwards;
        }
      `}</style>

      
      {/* Advertencia si no está conectado */}
      {!isConnected && (
        <div className="mb-3 p-3 bg-orange-500/20 border border-orange-500/50 rounded-lg text-center">
          <p className="text-orange-400 font-medium text-sm">⚠️ Conecta el ESP32</p>
        </div>
      )}

      {/* Contenedor Unificado: Secuencia + Controles */}
      <div className="bg-slate-700/50 rounded-xl p-3 flex gap-4">
        
        {/* IZQUIERDA: Visualización Secuencia */}
        <div className="flex-1 flex flex-col items-center min-w-0">
          <div className="text-slate-400 text-sm mb-2">Secuencia de Posiciones</div>
          
          <div className="flex items-center justify-center w-full gap-2 relative h-20">
            
            {/* Botón Anterior */}
            <button 
              onClick={() => setIndice(posicionActualIndex - 1)}
              disabled={posicionActualIndex <= 0}
              className="absolute left-0 p-3 rounded-full bg-slate-600/50 hover:bg-slate-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-white z-20"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Indicador de Clavos Restantes (Solo modo clavado) */}
            {secuenciaSeleccionada?.posiciones?.length === 250 && (
              <div className="absolute top-[-10px] right-[-10px] bg-orange-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg z-30 animate-in fade-in zoom-in">
                {clavosRestantes} 🔨
              </div>
            )}

            {/* Previous 2 (Oculto en móvil/espacio reducido) */}
            <div className={`flex flex-col items-center gap-1 transition-all hidden md:flex ${posicionActualIndex - 2 < 0 ? 'opacity-0' : 'opacity-40'}`}>
              <div className="text-sm font-mono text-slate-600">
                {posicionActualIndex - 2 >= 0 ? String(secuenciaSeleccionada.posiciones[posicionActualIndex - 2]).padStart(3, '0') : '---'}
              </div>
              {posicionActualIndex - 2 >= 0 && (
                <div className={`w-1.5 h-1.5 rounded-full ${
                  posicionesEjecutadas.includes(posicionActualIndex - 2) ? 'bg-green-500' : 'bg-slate-600'
                }`}></div>
              )}
            </div>
            
            {/* Previous 1 */}
            <div className={`flex flex-col items-center gap-1 transition-all ${posicionActualIndex - 1 < 0 ? 'opacity-0' : 'opacity-60'}`}>
              <div className="text-xl md:text-2xl font-mono text-slate-500">
                {posicionActualIndex - 1 >= 0 ? String(secuenciaSeleccionada.posiciones[posicionActualIndex - 1]).padStart(3, '0') : '---'}
              </div>
              {posicionActualIndex - 1 >= 0 && (
                <div className={`w-2 h-2 rounded-full ${
                  posicionesEjecutadas.includes(posicionActualIndex - 1) ? 'bg-green-500' : 'bg-slate-600'
                }`}></div>
              )}
            </div>

            {/* Current */}
            <div className="flex flex-col items-center z-10 mx-2 overflow-hidden">
              <div 
                key={posicionActualIndex}
                className={`text-5xl md:text-6xl font-bold font-mono text-blue-400 drop-shadow-2xl ${animationClass}`}
              >
                {String(secuenciaSeleccionada.posiciones[posicionActualIndex]).padStart(3, '0')}
              </div>
            </div>

            {/* Next 1 */}
            <div className={`flex flex-col items-center gap-1 transition-all ${posicionActualIndex + 1 >= secuenciaSeleccionada.posiciones.length ? 'opacity-0' : 'opacity-60'}`}>
              <div className="text-xl md:text-2xl font-mono text-slate-500">
                {posicionActualIndex + 1 < secuenciaSeleccionada.posiciones.length ? String(secuenciaSeleccionada.posiciones[posicionActualIndex + 1]).padStart(3, '0') : '---'}
              </div>
              {posicionActualIndex + 1 < secuenciaSeleccionada.posiciones.length && (
                <div className={`w-2 h-2 rounded-full ${
                  posicionesEjecutadas.includes(posicionActualIndex + 1) ? 'bg-green-500' : 'bg-slate-600'
                }`}></div>
              )}
            </div>

            {/* Next 2 */}
            <div className={`flex flex-col items-center gap-1 transition-all hidden md:flex ${posicionActualIndex + 2 >= secuenciaSeleccionada.posiciones.length ? 'opacity-0' : 'opacity-40'}`}>
              <div className="text-sm font-mono text-slate-600">
                {posicionActualIndex + 2 < secuenciaSeleccionada.posiciones.length ? String(secuenciaSeleccionada.posiciones[posicionActualIndex + 2]).padStart(3, '0') : '---'}
              </div>
              {posicionActualIndex + 2 < secuenciaSeleccionada.posiciones.length && (
                <div className={`w-1.5 h-1.5 rounded-full ${
                  posicionesEjecutadas.includes(posicionActualIndex + 2) ? 'bg-green-500' : 'bg-slate-600'
                }`}></div>
              )}
            </div>

            {/* Botón Siguiente */}
            <button 
              onClick={() => setIndice(posicionActualIndex + 1)}
              disabled={posicionActualIndex >= secuenciaSeleccionada.posiciones.length - 1}
              className="absolute right-0 p-3 rounded-full bg-slate-600/50 hover:bg-slate-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-white z-20"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="mt-3 flex items-center justify-center gap-4 w-full relative">
             {/* Status Button */}
            <div className="flex flex-col gap-1 items-center">
              <button 
                onClick={onToggleStatus}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full transition-all cursor-pointer hover:scale-105 ${
                  posicionesEjecutadas.includes(posicionActualIndex) 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-slate-600/30 text-slate-400 border border-slate-600/30'
                }`}
              >
                <span className="text-xs font-medium whitespace-nowrap">
                  {posicionesEjecutadas.includes(posicionActualIndex) ? '✅ Listo' : '⏳ Pendiente'}
                </span>
              </button>
            </div>

             {/* Index Edit */}
            <div className="text-slate-400 text-sm flex items-center">
                {isEditingIndex ? (
                <form onSubmit={handleIndexSubmit} className="flex items-center gap-1">
                    <input
                    type="number"
                    min="1"
                    max={secuenciaSeleccionada.posiciones.length}
                    value={tempIndex}
                    onChange={(e) => setTempIndex(e.target.value)}
                    className="w-12 px-1 py-0.5 bg-slate-800 border border-slate-600 rounded text-white text-center text-sm focus:outline-none focus:border-blue-500"
                    autoFocus
                    onBlur={() => setIsEditingIndex(false)}
                    />
                </form>
                ) : (
                <button 
                    onClick={() => {
                    setTempIndex(String(posicionActualIndex + 1));
                    setIsEditingIndex(true);
                    }}
                    className="flex items-center gap-1 hover:bg-slate-600/30 px-2 py-1 rounded transition-colors group text-xs relative"
                >
                    <span>{posicionActualIndex + 1}/{secuenciaSeleccionada.posiciones.length}</span>
                    <Edit3 size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400" />
                    
                    {/* Estimación discreta de tiempo */}
                    {ejecutando && tiempoRestante !== null && (
                      <span className="ml-3 text-[10px] text-slate-500 italic opacity-80 whitespace-nowrap">
                        ~{Math.ceil(tiempoRestante / 60)} min
                      </span>
                    )}
                </button>
                )}
            </div>
          </div>
        </div>

        {/* DERECHA: Controles */}
        <div className="flex flex-col justify-center gap-3 border-l border-slate-600 pl-4 min-w-[140px]">
          
           {/* Botón de acción */}
          <button
            onClick={ejecutando && !pausado ? handlePause : handlePlay}
            disabled={!isConnected}
            className={`w-full h-20 rounded-xl font-bold text-white transition-all flex flex-col items-center justify-center text-lg shadow-lg ${
              ejecutando && !pausado
                ? 'bg-yellow-500 hover:bg-yellow-600'
                : 'bg-green-500 hover:bg-green-600'
            } disabled:bg-gray-600 disabled:cursor-not-allowed`}
          >
            {ejecutando && !pausado ? (
              <>
                <Pause size={28} className="mb-1"/>
                Pausar
              </>
            ) : (
              <>
                <Play size={28} className="mb-1 ml-1"/>
                {pausado ? 'Reanudar' : 'Iniciar'}
              </>
            )}
          </button>

          {/* Estado */}
          <div className="p-2 bg-slate-800/50 rounded-lg flex items-center justify-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${
              ejecutando
                ? pausado
                  ? 'bg-yellow-500'
                  : esperandoTimelapse
                    ? 'bg-purple-500 animate-pulse'
                    : 'bg-green-500 animate-pulse'
                : 'bg-gray-500'
            }`}></div>
            <span className="text-slate-300 truncate font-medium">
              {ejecutando
                ? pausado
                  ? 'Pausado'
                  : esperandoTimelapse
                    ? '📸 En pos. 0...'
                    : esperandoAccion
                      ? 'Ejecutando...'
                      : 'En curso'
                : 'Detenido'}
            </span>
          </div>
        </div>
      </div>

      {/* Timelapse controls — solo admin + hilador */}
      {isAdmin && esHilador && (
        <div className="mt-2 bg-slate-700/30 rounded-xl border border-slate-600/30 px-3 py-2 flex items-center gap-3">
          <button
            onClick={() => setModoTimelapse(!modoTimelapse)}
            disabled={ejecutando && !pausado}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              modoTimelapse
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                : 'bg-slate-600/30 text-slate-400 border border-slate-600/30 hover:text-slate-200'
            } ${ejecutando && !pausado ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Camera size={14} />
            Timelapse
          </button>
          {modoTimelapse && (
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">Espera en 0:</span>
              <input
                type="text"
                inputMode="decimal"
                value={tlInput}
                onChange={(e) => {
                  // Permitir vacío y decimales parciales (ej: "1.", "0.5")
                  const raw = e.target.value;
                  if (raw === '' || /^\d*\.?\d*$/.test(raw)) setTlInput(raw);
                }}
                onBlur={commitTimelapse}
                disabled={ejecutando && !pausado}
                className="w-14 bg-slate-800 border border-slate-600 text-white text-center font-mono text-sm rounded-lg px-1 py-1 focus:outline-none focus:border-purple-500 disabled:opacity-50"
              />
              <span className="text-slate-500 text-xs">seg</span>
            </div>
          )}
          {modoTimelapse && (
            <div className="flex items-center gap-2" title="Intervalo máximo: al final, 1 foto cada iMax hiladas (curva cuadrática)">
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">iMax:</span>
              <input
                type="text"
                inputMode="decimal"
                value={imaxInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || /^\d*\.?\d*$/.test(raw)) setImaxInput(raw);
                }}
                onBlur={commitImax}
                disabled={ejecutando && !pausado}
                className="w-14 bg-slate-800 border border-slate-600 text-white text-center font-mono text-sm rounded-lg px-1 py-1 focus:outline-none focus:border-purple-500 disabled:opacity-50"
              />
              <span className="text-slate-500 text-xs">foto/hiladas</span>
            </div>
          )}
          {modoTimelapse && (
            <span className="text-purple-400/60 text-[10px] ml-auto italic">
              1 foto/hilada al inicio → 1 cada {Math.round(fotoIntervaloMax || 10)} al final
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoTestController;
